import React from 'react';
import { Container, Typography, Box, Link, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy = () => {
    const effectiveDate = 'January 6, 2026';

    return (
        <Box sx={{
            minHeight: '100vh',
            background: '#0a0e17',
            py: 6
        }}>
            <Container maxWidth="md">
                <Link
                    component={RouterLink}
                    to="/"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        color: '#94a3b8',
                        textDecoration: 'none',
                        mb: 4,
                        '&:hover': { color: '#fff' }
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                    Back to GammaFlow
                </Link>

                <Paper sx={{
                    p: { xs: 3, md: 5 },
                    background: 'rgba(30, 41, 59, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 2
                }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 4 }}>
                        Effective Date: {effectiveDate}
                    </Typography>

                    <Box sx={{ color: '#cbd5e1', '& h6': { color: '#fff', mt: 4, mb: 2, fontWeight: 600 }, '& p': { mb: 2, lineHeight: 1.7 } }}>
                        <Typography variant="h6">1. Introduction</Typography>
                        <Typography>
                            GammaFlow ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Service.
                        </Typography>

                        <Typography variant="h6">2. Information We Collect</Typography>
                        <Typography>
                            <strong>Authentication Data:</strong> When you sign in through Whop, we receive your Whop user ID and basic profile information necessary to verify your subscription status.
                        </Typography>
                        <Typography>
                            <strong>Usage Data:</strong> We may collect information about how you interact with our Service, including pages viewed, features used, and timestamps.
                        </Typography>
                        <Typography>
                            <strong>Technical Data:</strong> We may collect browser type, device information, and IP address for security and analytics purposes.
                        </Typography>

                        <Typography variant="h6">3. How We Use Your Information</Typography>
                        <Typography>
                            We use your information to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Verify your subscription status and provide access to the Service</li>
                            <li>Improve and optimize our Service</li>
                            <li>Ensure security and prevent fraud</li>
                            <li>Communicate important updates about the Service</li>
                        </Box>

                        <Typography variant="h6">4. Third-Party Services</Typography>
                        <Typography>
                            <strong>Whop:</strong> Authentication and subscription management are handled through Whop. Your payment information is processed by Whop, not by us. Please review Whop's Privacy Policy for details on their data practices.
                        </Typography>
                        <Typography>
                            <strong>Market Data Providers:</strong> We use third-party APIs (such as Tradier) to fetch market data. These services do not receive any of your personal information.
                        </Typography>

                        <Typography variant="h6">5. Cookies and Local Storage</Typography>
                        <Typography>
                            We use browser local storage to maintain your authentication session (JWT tokens). We do not use tracking cookies for advertising purposes.
                        </Typography>

                        <Typography variant="h6">6. Data Retention</Typography>
                        <Typography>
                            We retain your authentication data for as long as you maintain an active subscription. Usage data may be retained in anonymized form for analytics purposes.
                        </Typography>

                        <Typography variant="h6">7. Data Security</Typography>
                        <Typography>
                            We implement industry-standard security measures to protect your data, including encrypted connections (HTTPS) and secure token-based authentication.
                        </Typography>

                        <Typography variant="h6">8. Your Rights</Typography>
                        <Typography>
                            Depending on your jurisdiction, you may have rights to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Access your personal data</li>
                            <li>Request correction of inaccurate data</li>
                            <li>Request deletion of your data</li>
                            <li>Object to processing of your data</li>
                        </Box>
                        <Typography>
                            To exercise these rights, please contact us through our support channels.
                        </Typography>

                        <Typography variant="h6">9. Children's Privacy</Typography>
                        <Typography>
                            Our Service is not intended for users under 18 years of age. We do not knowingly collect personal information from minors.
                        </Typography>

                        <Typography variant="h6">10. Changes to This Policy</Typography>
                        <Typography>
                            We may update this Privacy Policy from time to time. We will notify users of significant changes through the Service or other means.
                        </Typography>

                        <Typography variant="h6">11. Contact Us</Typography>
                        <Typography>
                            For questions about this Privacy Policy or our data practices, please contact us through our Whop page or support channels.
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
